import * as React from "react";
import { DropdownMenu } from "@conserveit/blocks";
import { useState as useWorkflowState } from "../../../context/reports/workflow";
import { SingleBar } from "./types";
import { WorkflowDatagrid, GridProps } from "./components/WorkflowDatagrid";
import { RankingBarChart } from "../../Charts/components/RankingBarChart";
import * as Chart from "../ChartZone.styles";
import { DatagridWrapper } from "./Workflow.styles";
import { Sorting } from "../../../context/reports/types";
import { getReadableMetricsType } from "./utils";
import { useTrackedActions } from "./hooks/useTrackedActions";
import { NoResult } from "../";
import { ChartLoader } from "../../Charts/components/ChartLoader";
import { COLORS_RAW } from "../../AppChrome/App.constants";
import { ChartMargin } from "../../Layout/Page";
import EventEmitter from "../../../utils/eventEmitter";

interface Props {
  bars: SingleBar[];
  gridData: GridProps;
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
  siteLabel: string;
}

interface Datum {
  name: string;
  value: number;
}

const chartTitle = "Site workflow performance";

const VALUE_SUFFIX = " actions closed";
const formatTooltipValue = (value: number) =>
  `${Math.round(value)}${VALUE_SUFFIX}`;
const formatValueLabel = (value: number) => `${value}`;

export const getDataForSingleSite = (bars?: SingleBar[]): Datum[] => {
  if (!bars) return [];
  return bars.map((b) => ({ name: b.label, value: b.value }));
};

// not used for now
const getDataForMultipleSites = (
  siteLabel: string,
  bars?: SingleBar[]
): Datum[] => {
  if (!bars) return [];

  const sumClosedActions: number = bars
    .map((b) => b.value)
    .reduce((acc, curr) => acc + curr, 0);

  return [{ name: siteLabel, value: sumClosedActions }];
};

export const WFSite: React.FC<Props> = ({
  bars,
  gridData,
  selectedSites,
  siteLabel,
}) => {
  const state = useWorkflowState();
  const { trackedOrderBy } = useTrackedActions();
  const chartData =
    selectedSites.length === 1
      ? getDataForSingleSite(bars)
      : getDataForMultipleSites(siteLabel, bars);

  return (
    <>
      <Chart.Container>
        <Chart.Block>
          <Chart.Header>
            <Chart.Heading>
              <Chart.Title>{chartTitle}</Chart.Title>
              <Chart.Subtitle>
                Shown as number of Actions{" "}
                <em>
                  {getReadableMetricsType(state.metrics.currentMetricsType)}
                </em>{" "}
                ({gridData.count})
              </Chart.Subtitle>
            </Chart.Heading>
            <Chart.Controls>
              <DropdownMenu
                right
                trigger="Order"
                defaultValue={state.sortingDirection}
                value={state.sortingDirection}
                onSelect={trackedOrderBy(chartTitle)}
                options={[
                  { value: Sorting.Desc, label: "Most to Least" },
                  { value: Sorting.Asc, label: "Least to Most" },
                ]}
              />
            </Chart.Controls>
          </Chart.Header>
          <Chart.Content>
            {state.metricsLoading ? (
              <ChartLoader />
            ) : gridData.count !== 0 ? (
              <>
                {selectedSites.length === 1 ? (
                  <>
                    <ChartMargin>
                      <RankingBarChart
                        data={chartData}
                        color={COLORS_RAW.TEAL_001}
                        xAxisTitle="Actions closed"
                        tooltipFormatter={formatTooltipValue}
                        valueLabelFormatter={formatValueLabel}
                      />
                    </ChartMargin>
                    <DatagridWrapper>
                      <WorkflowDatagrid
                        trackingTitle={chartTitle}
                        groupRowLabel="Team"
                        records={gridData.records}
                        count={gridData.count}
                        critical={gridData.critical}
                        eventEmitter={new EventEmitter()}
                      />
                    </DatagridWrapper>
                  </>
                ) : (
                  <ChartMargin>
                    <RankingBarChart
                      data={chartData}
                      color={COLORS_RAW.TEAL_001}
                      xAxisTitle="Actions closed"
                      tooltipFormatter={formatTooltipValue}
                      valueLabelFormatter={formatValueLabel}
                    />
                  </ChartMargin>
                )}
              </>
            ) : (
              <NoResult />
            )}
          </Chart.Content>
        </Chart.Block>
      </Chart.Container>
    </>
  );
};
